import {getLegendOverrideKey} from '../../util/plotHelpers/legend';
import {type Line} from '../../util/plotHelpers/types';
import {
  type ChartData,
  type ChartGeometry,
  type ChartMetadata,
  type ChartSeries,
  type ChartStatistic,
  flattenChartGeometry,
  shallowReuse,
} from '../vis/chartSeries';

const emptyData: ChartData = {
  series: [],
  seriesStylesById: {},
  seriesMetadataById: {},
};

function getRole(line: Line): ChartSeries['role'] {
  if (!line.aux) {
    return 'primary';
  }
  if (line.type === 'area') {
    return 'band';
  }
  return line.meta?.aggregation === 'samples' ? 'sample' : 'original';
}

/** A second primary with the same key makes the relationship ambiguous. */
function addParent<K>(parents: Map<K, number | null>, key: K, index: number) {
  parents.set(
    key,
    parents.has(key) && parents.get(key) !== index ? null : index
  );
}

function prepareStatistic(value: Line | undefined): ChartStatistic | undefined {
  return value == null ? undefined : {data: value.data, stddev: value.stddev};
}

/**
 * Normalize prepared legacy lines without retaining their application records.
 * Point arrays are borrowed immutable snapshots; only their references are compared.
 * The previous neutral snapshot is the sole reconciliation input.
 */
export function prepareChartSeries(
  lines: Line[],
  previous: ChartData = emptyData
): ChartData {
  const previousById = new Map(
    previous.series.map(series => [series.seriesId, series])
  );
  const occurrences = new Map<string, number>();
  const identities = lines.map(line => {
    const scope = [
      line.run?.project?.entityName ?? null,
      line.run?.project?.name ?? null,
      line.run?.runsetInfo?.id ?? null,
    ];
    const family = JSON.stringify([
      ...scope,
      line.uniqueId ?? line.run?.name ?? line.name ?? null,
      line.metricName ?? null,
    ]);
    const nameFamily =
      line.name == null
        ? null
        : JSON.stringify([...scope, line.name, line.metricName ?? null]);
    const role = getRole(line);
    const memberId =
      role === 'sample' ? (line.run?.name ?? line.name ?? null) : null;
    const variant = [
      family,
      role,
      line.type ?? null,
      line.meta?.aggregation ?? 'none',
      line.smoothed === true,
      memberId,
    ];
    const variantKey = JSON.stringify(variant);
    // Legacy inputs can lack a distinguishing source key. Only those otherwise
    // identical occurrences depend on order; keyed sample members do not.
    const occurrence = occurrences.get(variantKey) ?? 0;
    occurrences.set(variantKey, occurrence + 1);
    return {
      seriesId: JSON.stringify([...variant, occurrence]),
      family,
      nameFamily,
      role,
      memberId,
    };
  });

  const primaryByFamily = new Map<string, number | null>();
  const primaryByName = new Map<string, number | null>();
  const primaryByStatistic = new Map<Line, number | null>();
  lines.forEach((line, index) => {
    const identity = identities[index];
    if (identity.role !== 'primary') {
      return;
    }
    addParent(primaryByFamily, identity.family, index);
    if (identity.nameFamily != null) {
      addParent(primaryByName, identity.nameFamily, index);
    }
    for (const statistic of [
      line.minmaxLine,
      line.stddevLine,
      line.stderrLine,
    ]) {
      if (statistic != null) {
        addParent(primaryByStatistic, statistic, index);
      }
    }
  });
  const parentIndexes = lines.map((line, index) => {
    const identity = identities[index];
    if (identity.role === 'primary') {
      return null;
    }
    // Explicit associations take precedence; an ambiguous key does not fall
    // through to a weaker key and accidentally attach to a different primary.
    if (primaryByStatistic.has(line)) {
      return primaryByStatistic.get(line) ?? null;
    }
    if (primaryByFamily.has(identity.family)) {
      return primaryByFamily.get(identity.family) ?? null;
    }
    return identity.nameFamily == null
      ? null
      : (primaryByName.get(identity.nameFamily) ?? null);
  });
  const metadataIds = identities.map((identity, index) => {
    const parent = parentIndexes[index];
    const owner =
      identity.role !== 'sample' && parent != null
        ? identities[parent]
        : identity;
    return JSON.stringify(['metadata', owner.seriesId]);
  });

  const seriesMetadataById: ChartData['seriesMetadataById'] = {};
  lines.forEach((line, index) => {
    if (parentIndexes[index] != null && identities[index].role !== 'sample') {
      return;
    }
    const fancy = line.fancyTitle;
    const metadata: ChartMetadata = {
      displayName: line.displayName,
      title: line.title,
      runName: line.run?.name,
      isPinned: line.run?.isPinned === true,
      legendHref:
        (fancy?.groupKeys?.length ?? 0) === 0 &&
        fancy?.rootUrl != null &&
        fancy.run?.name != null
          ? `${fancy.rootUrl}/${fancy.run.name}`
          : undefined,
      entityName: line.run?.project?.entityName,
      projectName: line.run?.project?.name,
    };
    const id = metadataIds[index];
    seriesMetadataById[id] = shallowReuse(
      metadata,
      previous.seriesMetadataById[id]
    );
  });

  const seriesStylesById: ChartData['seriesStylesById'] = {};
  const series = lines.map((line, index): ChartSeries => {
    const identity = identities[index];
    const old = previousById.get(identity.seriesId);
    const geometry: ChartGeometry = {
      data: line.data,
      nanPoints: line.nanPoints,
      domain: line.domain,
      timestep: line.timestep,
      smoothed: line.smoothed === true,
      smoothedFromBackend: line.smoothedLineFromBackend != null,
      statistics: {
        minmax: prepareStatistic(line.minmaxLine),
        stddev: prepareStatistic(line.stddevLine),
        stderr: prepareStatistic(line.stderrLine),
      },
    };
    seriesStylesById[identity.seriesId] = shallowReuse(
      {
        color: line.color,
        lineWidth: line.lineWidth,
        mark: line.mark,
        colorKey: getLegendOverrideKey(line),
      },
      previous.seriesStylesById[identity.seriesId]
    );
    const parent = parentIndexes[index];
    return shallowReuse(
      {
        seriesId: identity.seriesId,
        metadataId: metadataIds[index],
        highlightId: line.uniqueId ?? null,
        highlightName: line.name ?? null,
        memberId: identity.memberId,
        parentSeriesId: parent == null ? null : identities[parent].seriesId,
        role: identity.role,
        kind: line.type,
        metricName: line.metricName,
        isBaseline: line.run?.isBaseline === true,
        isGrouped: (line.groupKeys?.length ?? 0) > 0,
        aggregation: line.meta?.aggregation ?? 'none',
        category: line.meta?.category ?? 'default',
        mode: line.meta?.mode ?? 'sampled',
        ...flattenChartGeometry(geometry),
      },
      old
    );
  });
  return shallowReuse(
    {
      series: shallowReuse(series, previous.series),
      seriesStylesById: shallowReuse(
        seriesStylesById,
        previous.seriesStylesById
      ),
      seriesMetadataById: shallowReuse(
        seriesMetadataById,
        previous.seriesMetadataById
      ),
    },
    previous
  );
}
